import { useCallback, useEffect, useState } from "react";
import { API_URL, SITE_URL, useAuth } from "./AuthContext";

// The site's sign-up page reads `ref` and records it as the new trader's referrer
export const referralLink = (code) => `${SITE_URL}/signup?ref=${encodeURIComponent(code)}`;

// Referral program state for the signed-in trader (GET /api/referrals): their link
// code, the code they signed up with, summary figures and referral history.
// `applyCode` and `withdraw` post to the API and resolve to { error } on failure, so
// the modals can show the message inline.
export default function useReferrals() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  const load = useCallback(
    (signal) => {
      setStatus((prev) => (prev === "ready" ? prev : "loading"));
      return fetch(`${API_URL}/api/referrals`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((body) => {
          setData(body);
          setStatus("ready");
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error("Failed to load referrals:", err);
          setStatus((prev) => (prev === "ready" ? prev : "error"));
        });
    },
    [token]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const post = useCallback(
    async (path, body) => {
      try {
        const res = await fetch(`${API_URL}/api/referrals${path}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) return { error: json.error || "Something went wrong. Please try again." };
        return json;
      } catch {
        return { error: "Couldn't reach the server. Check your connection and try again." };
      }
    },
    [token]
  );

  const applyCode = useCallback(
    async (code) => {
      const result = await post("/code", { code });
      if (!result.error) setData((prev) => prev && { ...prev, referredBy: result.referredBy });
      return result;
    },
    [post]
  );

  const withdraw = useCallback(
    async ({ amount, address }) => {
      const result = await post("/withdrawals", { amount, address });
      if (result.referrals) setData(result.referrals);
      return result;
    },
    [post]
  );

  return { data, status, reload: () => load(), applyCode, withdraw };
}
